import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Flame, ArrowUpRight, Instagram, Twitter, Youtube } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-black overflow-hidden">
      {/* Gradient Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-lime-500/10 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-lime-500/5 rounded-full blur-[150px]" />
      
      {/* Newsletter Section */}
      <div className="relative border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h3 className="text-3xl font-black text-white mb-2">
                Stay in the <span className="text-lime-400">Game</span>
              </h3>
              <p className="text-white/60">Get updates on new products and exclusive offers.</p>
            </div>
            <div className="flex w-full max-w-md">
              <input 
                type="email" 
                placeholder="Enter your email"
                className="flex-1 px-5 py-4 bg-white/5 border border-white/10 rounded-l-xl text-white placeholder-white/40 focus:outline-none focus:border-lime-400/50"
              />
              <button className="px-6 py-4 bg-lime-400 hover:bg-lime-300 text-black font-bold rounded-r-xl transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <span className="text-white font-black text-2xl tracking-tight">
                ACTIVE<span className="text-lime-400">ONYX</span> FIT
              </span>
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-sm">
              Premium boxing, MMA and combat-sports gear engineered for athletes who train hard and live the lifestyle. Glove up. Step in. Go to work.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              {[Instagram, Twitter, Youtube].map((Icon, i) => (
                <a 
                  key={i}
                  href="#"
                  className="w-10 h-10 bg-white/5 hover:bg-lime-400/20 border border-white/10 hover:border-lime-400/50 rounded-xl flex items-center justify-center text-white/60 hover:text-lime-400 transition-all"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Shop</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/shop" className="text-white/50 hover:text-lime-400 text-sm transition-colors flex items-center gap-1 group">
                  All Products
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              {categories.slice(0, 4).map((cat) => (
                <li key={cat.id}>
                  <Link 
                    to={`/shop?category=${cat.slug}`}
                    className="text-white/50 hover:text-lime-400 text-sm transition-colors flex items-center gap-1 group"
                  >
                    {cat.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Support</h4>
            <ul className="space-y-3">
              {[
                { name: 'Shipping', path: '/shipping' },
                { name: 'Returns', path: '/returns' },
                { name: 'FAQ', path: '/faq' },
                { name: 'Privacy', path: '/privacy' },
                { name: 'Terms', path: '/terms' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/50 hover:text-lime-400 text-sm transition-colors flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-start gap-3 text-white/50 hover:text-lime-400 transition-colors group"
                >
                  <Phone className="w-4 h-4 mt-0.5 text-lime-400/60 group-hover:text-lime-400" />
                  <span className="text-sm">{CONTACT_INFO.phone}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-start gap-3 text-white/50 hover:text-lime-400 transition-colors group"
                >
                  <Mail className="w-4 h-4 mt-0.5 text-lime-400/60 group-hover:text-lime-400" />
                  <span className="text-sm break-all">{CONTACT_INFO.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/50">
                  <MapPin className="w-4 h-4 mt-0.5 text-lime-400/60 flex-shrink-0" />
                  <span className="text-sm">
                    {CONTACT_INFO.address.street}<br />
                    {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Left */}
            <div className="flex flex-col sm:flex-row items-center gap-4 text-sm">
              <span className="text-white/40">© {currentYear} Active Onyx Fit</span>
              <span className="hidden sm:block text-white/20">•</span>
              <span className="text-white/40">{CONTACT_INFO.corpName}</span>
            </div>

            {/* Center - Payment */}
            <div className="flex items-center gap-3">
              <span className="text-white/30 text-xs">We Accept</span>
              <div className="flex gap-2">
                {['Visa', 'MC', 'Amex', 'Disc'].map((card) => (
                  <div key={card} className="px-2 py-1 bg-white/10 rounded text-[10px] text-white/60 font-medium">
                    {card}
                  </div>
                ))}
              </div>
            </div>

            {/* Right */}
            <div className="text-xs text-white/30">
              Charges appear as "<span className="text-lime-400/60">{CARD_DESCRIPTOR}</span>"
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
